import { Item } from '../../domain/item';
import { RapidCaptureRecord, hasCaptureEdits } from './rapidCaptureTypes';
import { isMatchingRapidCaptureItem } from './rapidCaptureReconciliation';

export interface CaptureActivityPresentation {
  group: 'waiting' | 'adding' | 'analysis' | 'failed' | 'confirming';
  label: string;
  detail?: string;
  severity: 'info' | 'warning' | 'error';
  canRetry: boolean;
  retryLabel?: string;
  canDiscard: boolean;
  canOpenItem: boolean;
  canDismissAI?: boolean;
}

export const captureActivityPresentation = (
  record: RapidCaptureRecord,
  online: boolean,
  item?: Item,
): CaptureActivityPresentation => {
  const base = {
    severity: 'info' as const,
    canRetry: false,
    canDiscard: Boolean(
      (record.mutation || hasCaptureEdits(record)) &&
      (online ||
        (record.itemCreated
          ? !record.editSnapshot
          : !record.itemCreateAttempted)),
    ),
    canOpenItem: Boolean(
      (record.itemCreated || record.mutation) &&
      isMatchingRapidCaptureItem(record, item),
    ),
  };
  if (record.state === 'terminal')
    return {
      ...base,
      group: 'failed',
      label: 'Capture access changed',
      detail: record.lastError?.message,
      severity: 'warning',
      canDiscard: true,
    };
  if (record.schemaVersion !== 6)
    return {
      ...base,
      group: 'failed',
      label: 'Older queued capture',
      detail: 'This capture needs explicit recovery. Its photo is retained.',
      severity: 'warning',
      canRetry: online,
      retryLabel: 'Recover photo',
      canDiscard: true,
    };
  if (record.itemUnavailable)
    return {
      ...base,
      canOpenItem: false,
      group: 'failed',
      label: 'Saved item is no longer available',
      detail:
        'This item was deleted or moved. Local changes are retained here until you discard them.',
      severity: 'warning',
      canDiscard: true,
    };
  if (record.state === 'failed') {
    const warning =
      [
        'capacity',
        'authentication',
        'permission',
        'conflict',
        'quota',
      ].includes(record.lastError?.category || '') ||
      record.aiSkipReason === 'cost-changed';
    return {
      ...base,
      group: 'failed',
      label:
        record.mutation || record.lastError?.phase === 'edits'
          ? 'Changes need attention'
          : record.itemCreated
            ? 'Keywords need attention'
            : 'Upload needs attention',
      detail:
        record.aiSkipReason === 'cost-changed' &&
        record.pendingAiCredits !== undefined
          ? `Confirm ${record.pendingAiCredits} credits to generate keywords.`
          : record.lastError?.message,
      severity: warning ? 'warning' : 'error',
      canRetry: online,
      retryLabel:
        record.lastError?.phase === 'edits'
          ? 'Retry changes'
          : record.aiSkipReason === 'cost-changed'
            ? record.pendingAiCredits === undefined
              ? 'Review cost'
              : 'Confirm & retry'
            : record.itemCreated
              ? 'Retry keywords'
              : 'Retry upload',
      canDiscard:
        (Boolean(record.mutation) ||
          hasCaptureEdits(record) ||
          !record.itemCreated) &&
        (online || !record.itemCreateAttempted),
      canDismissAI:
        !record.mutation &&
        record.itemCreated &&
        record.lastError?.phase !== 'edits',
    };
  }
  if (record.itemCreated && record.aiSkipReason === 'cost-unconfirmed')
    return {
      ...base,
      group: 'failed',
      label: 'Keywords were not generated',
      detail:
        'The item was saved. Retry keyword generation when AI is available.',
      severity: 'warning',
      canDismissAI: true,
      canRetry: online,
      retryLabel: 'Retry keywords',
    };
  if (
    record.itemCreated &&
    (record.localRevision || 0) > (record.syncedRevision || 0)
  )
    return {
      ...base,
      group: 'confirming',
      label: online ? 'Syncing edits' : 'Changes saved on this device',
    };
  if (record.state === 'confirming')
    return { ...base, group: 'confirming', label: 'Item ready' };
  if (record.itemCreated && record.analyzeImage)
    return { ...base, group: 'analysis', label: 'Generating keywords' };
  if (record.state === 'submitting')
    return {
      ...base,
      group: 'adding',
      label:
        record.mutation?.imageAction === 'keep'
          ? 'Saving changes'
          : 'Uploading',
    };
  if (record.serverAccepted)
    return { ...base, group: 'adding', label: 'Saving item' };
  return {
    ...base,
    group: 'waiting',
    label: online
      ? record.mutation
        ? 'Waiting to sync changes'
        : 'Waiting to upload'
      : 'Waiting for connection',
  };
};
